import { useEffect, useState } from 'react';

import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { RecoilRoot } from 'recoil';

import { Contact } from './types';

import {
  ContactType,
  getContacts,
  saveContact,
  updateContact,
  deleteContact
} from 'RestAPIs/contacts/contacts';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';

import CaoSection from './CaoSection';
import ContactCard from './ContactCard';
import ContactDialog, { ProcessedContact } from './ContactDialog';
import ContactsStyles from './ContactsStyles';

function Contacts() {
  const { t } = useTranslation('common');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [type, setType] = useState<ContactType>();
  const [showContactDialog, setShowContactDialog] = useState(false);
  const contactTypes = [
    ContactType.APPLICATION_MANAGER,
    ContactType.APPLICATION_OWNER,
    ContactType.ROSTER_PROCESS_MANAGER,
    ContactType.FLEX_PROCESS_MANAGER,
    ContactType.ICT_MANAGER,
    ContactType.DATABASE_VIEWS_API_INTEGRATIONS,
    ContactType.EXPORT_SALARY_ADMINISTRATOR,
    ContactType.EXPORT_FINANCIAL,
    ContactType.SECURITY_OFFICER
  ];

  const onContactSaved = (contact: ProcessedContact) => {
    if (contact.id) {
      updateContact(contact.id, {
        ...omit(contact, ['id']),
        type: contact.type as ContactType
      }).then(({ data }) => {
        const existingContacts = contacts.filter((c) => c.id !== contact.id);
        setContacts([...existingContacts, data]);
      });
    } else {
      saveContact({
        ...omit(contact, ['id']),
        type: contact.type as ContactType
      }).then(({ data }) => {
        setContacts([...contacts, data]);
      });
    }
  };

  const onContactDelete = (id: number) => {
    deleteContact(id).then(() => {
      setContacts(contacts.filter((c) => c.id !== id));
    });
  };

  useEffect(() => {
    getContacts().then(({ data }) => {
      setContacts(data);
    });
  }, []);

  return (
    <Box>
      <CaoSection />
      <ContactsStyles>
        <Button
          variant="contained"
          className="add-new-btn"
          onClick={() => {
            setType(undefined);
            setShowContactDialog(true);
          }}
        >
          {t('addContact')}
        </Button>
        <Table size="small">
          <TableBody>
            {contactTypes.map((contactType) => {
              const contactsByType = contacts.filter(
                ({ type }) => contactType === type
              );
              return (
                <TableRow key={contactType}>
                  <TableCell className="contact-type">
                    <div className="name">
                      {t(`contactTypes.${contactType}`)}
                    </div>
                  </TableCell>
                  <TableCell className="contact-cell">
                    {contactsByType.map((c) => (
                      <ContactCard
                        key={c.id}
                        {...c}
                        onContactSaved={onContactSaved}
                        onDeleteContact={onContactDelete}
                      />
                    ))}
                    {!contactsByType.length && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setType(contactType);
                          setShowContactDialog(true);
                        }}
                      >
                        {t('addContact')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {showContactDialog && (
          <ContactDialog
            type={type}
            onSave={onContactSaved}
            onHidden={() => setShowContactDialog(false)}
          />
        )}
      </ContactsStyles>
    </Box>
  );
}

export default function ContactsRoot() {
  return (
    <RecoilRoot>
      <Contacts />
    </RecoilRoot>
  );
}
